import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "../api";
import { Download, ClipboardList, Search } from "lucide-react";

const stageColor = {
  Pending: "#94a3b8", Screening: "#0ea5e9", Interview: "#f59e0b",
  Interviewing: "#0ea5e9", Offer: "#22c55e", Hired: "#10b981",
  Onboarded: "#006CFF", Rejected: "#dc2626", Withdrawn: "#9ca3af",
};

export default function CompanyApplicantHistory() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/recruitment/applicant-history")
      .then((r) => setRows(r.data))
      .catch((e) => setErr(formatError(e)));
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return null;
    if (!q.trim()) return rows;
    const needle = q.toLowerCase();
    return rows.filter((r) =>
      [r.candidate_name, r.candidate_email, r.job_title, r.location, r.last_stage]
        .some((s) => String(s || "").toLowerCase().includes(needle))
    );
  }, [rows, q]);

  const exportCsv = () => {
    if (!filtered?.length) return;
    const head = ["Candidate", "Email", "Phone", "Job", "Apply date", "Location", "Last stage", "Updated", "Comments"];
    const lines = [head.join(",")];
    for (const r of filtered) {
      lines.push([
        r.candidate_name, r.candidate_email, r.candidate_phone, r.job_title,
        r.apply_date, r.location, r.last_stage, r.updated_at, (r.comments || "").replace(/[",\n]/g, " "),
      ].map((v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "applicant-history.csv";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="applicant-history-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="eyebrow flex items-center gap-2"><ClipboardList size={12} /> Applicant history</span>
          <h2 className="font-display font-black text-2xl mt-1">Every candidate, every stage</h2>
          <p className="text-muted text-sm">Cross-posting view of all applicants who&apos;ve touched your hiring pipeline.</p>
        </div>
        <button className="btn-ghost" disabled={!filtered?.length} onClick={exportCsv} data-testid="export-csv-btn">
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="card card-pad flex items-center gap-3">
        <Search size={14} className="text-muted" />
        <input
          className="input flex-1" placeholder="Search by name, email, job, location, stage…"
          value={q} onChange={(e) => setQ(e.target.value)} data-testid="applicant-history-search"
        />
        <span className="text-xs text-muted">Total Records: <b className="text-default">{filtered?.length ?? 0}</b></span>
      </div>

      {err && <div className="card card-pad text-red-600">{err}</div>}

      <div className="card overflow-x-auto">
        <table className="dense">
          <thead>
            <tr><th>Candidate</th><th>Job</th><th>Apply Date</th><th>Location</th><th>Last Stage</th><th>Updated On</th><th>Comments</th></tr>
          </thead>
          <tbody data-testid="applicant-history-tbody">
            {filtered === null ? <tr><td colSpan={7} className="text-muted">Loading…</td></tr>
              : filtered.length === 0 ? <tr><td colSpan={7} className="text-muted">{rows?.length ? "No matches for your search." : "No applicants yet — post a job to start collecting candidates."}</td></tr>
              : filtered.map((r) => (
                <tr key={r.id} data-testid={`applicant-row-${r.id}`}>
                  <td>
                    <div className="font-semibold text-signal">{r.candidate_name}</div>
                    <div className="text-xs text-muted">{r.candidate_email}</div>
                    {r.candidate_phone ? <div className="text-xs text-muted">{r.candidate_phone}</div> : null}
                  </td>
                  <td>
                    <div className="font-semibold">{r.job_title}<span className="text-xs text-muted font-normal">({r.job_code})</span></div>
                  </td>
                  <td className="text-emerald-600">{String(r.apply_date || "").slice(0, 10) || "—"}</td>
                  <td>{r.location}</td>
                  <td>
                    <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: stageColor[r.last_stage] || "#64748b" }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: "currentColor" }} />
                      {r.last_stage}
                    </span>
                  </td>
                  <td className="text-muted text-xs">{String(r.updated_at || "").slice(0, 10) || "—"}</td>
                  <td className="text-muted text-xs max-w-[220px] truncate" title={r.comments}>{r.comments || "—"}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
